import { apiClient } from './client';
import type { Order } from '@/types/models';
import type { OrderStatus } from '@/types/enums';

// ===== Request / Response types =====
export interface QRScanRequest {
  order_id: number;
  ts: number;
  sig: string;
}

export interface QRScanResponse {
  order_id: number;
  order_number: string;
  status: OrderStatus;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  shelf_location: string | null;
  message: string;
}

export interface HandoffResponse {
  order_id: number;
  order_number: string;
  status: OrderStatus;
  message: string;
}

export interface HandoffItemRequest {
  order_item_id: number;
  quantity_handed_off: number;
}

export type HandoffSource = 'warehouse' | 'external';

export interface HandoffRequest {
  items: HandoffItemRequest[];
  handoff_notes?: string | null;
  source?: HandoffSource;
}

export interface QRPayload {
  order_id: number;
  ts: number;
  sig: string;
}

// ===== API =====
export const pickupApi = {
  /**
   * Verify a scanned QR code payload. Transitions order to PICKUP_IN_PROGRESS.
   */
  scanQR: async (payload: QRScanRequest): Promise<QRScanResponse> => {
    const response = await apiClient.post<QRScanResponse>('/v1/pickup/scan', payload);
    return response.data;
  },

  /**
   * Confirm handoff — PICKUP_IN_PROGRESS → HANDED_OFF.
   */
  confirmHandoff: async (orderId: number, request: HandoffRequest): Promise<HandoffResponse> => {
    const response = await apiClient.post<HandoffResponse>(`/v1/pickup/${orderId}/handoff`, request);
    return response.data;
  },

  /**
   * Get QR code image URL for an order (returns the API URL, not the image itself).
   */
  getQRImageUrl: (orderId: number): string => {
    const baseURL = apiClient.defaults.baseURL ?? '';
    return `${baseURL}/v1/pickup/orders/${orderId}/qr`;
  },

  /**
   * Search orders by query string (used for manual order lookup).
   */
  searchOrders: async (query: string): Promise<Order[]> => {
    const response = await apiClient.get<{ data: Order[] }>('/v1/orders', {
      params: { search: query, limit: 10 },
    });
    return response.data.data;
  },

  downloadHandoffPdf: async (orderId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v1/pickup/${orderId}/handoff-pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
